import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { FacetOperator } from "@utils/search/@types/searchConfiguration";
import { getInvalidationsForComponent } from "@utils/search/invalidation.utils";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    clearFacetValue,
    selectFacetValue,
} from "@redux/modules/value/search/search.actions";
import { getSelectedValuesForFacet } from "@redux/modules/value/search/search.selectors";

import {
    Facet,
    FacetComponentProps,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facet.types";
import {
    DefaultFacetValue,
    FacetType,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/@types/facetType.types";
import {
    CustomSortingFor,
    FACET_SHOWMORE_LIMIT,
    FACET_SORT_BY,
    getOperator,
} from "@components/Search/SearchResults/StateComponents/Facets/Facet/facet.configuration";
import { ShowMoreComponent } from "@components/Search/SearchResults/components/ShowMore/showMore.component";

import { DefaultFacetComponent } from "./defaultFacet.component";

export type DefaultFacetDisplayValue = {
    label: string;
    count: number;
    refined: boolean;
    onRefine: () => void;
};

const DefaultFacet = ({
    facet,
    component,
    sendEvent,
}: FacetComponentProps<FacetType.DEFAULT>) => {
    const [isShowingMore, setIsShowingMore] = useState(false);

    const dispatch = useThunkDispatch();

    const selectedValues = useSelector(
        getSelectedValuesForFacet(component, facet.name),
    );

    const operator = useMemo(() => getOperator(facet.name), [facet.name]);
    const invalidations = useMemo(
        () => getInvalidationsForComponent(component),
        [component],
    );

    const createOnRefine = useCallback(
        (refined: boolean, facetName: string, facetValue: string) => () =>
            dispatch(
                refined
                    ? clearFacetValue(
                          component,
                          facetName,
                          facetValue,
                          invalidations,
                      )
                    : selectFacetValue(
                          component,
                          {
                              name: facetName,
                              operator: operator,
                          },
                          facetValue,
                          invalidations,
                          sendEvent,
                      ),
            ),
        [component, operator, invalidations, dispatch],
    );

    const selectedValuesObj = selectedValues.reduce((acc, curr) => {
        acc[curr] = 0;
        return acc;
    }, {} as DefaultFacetValue);

    const customSorting = CustomSortingFor<Facet<FacetType.DEFAULT>>(facet);

    const defaultFacetDisplayValues: Array<DefaultFacetDisplayValue> =
        Object.entries({ ...selectedValuesObj, ...facet.values })
            .map(([facetValueName, count]) => {
                const refined = selectedValues.includes(facetValueName);
                return {
                    label: facetValueName,
                    count: count,
                    refined: refined,
                    onRefine: createOnRefine(
                        refined,
                        facet.name,
                        facetValueName,
                    ),
                };
            })
            .sort(customSorting || FACET_SORT_BY)
            .slice(0, !isShowingMore ? FACET_SHOWMORE_LIMIT : undefined);

    const handleToggleShowMore = () => setIsShowingMore(current => !current);
    const canShowMore =
        Object.keys(facet.values ?? {}).length > FACET_SHOWMORE_LIMIT;

    return (
        <>
            <DefaultFacetComponent
                values={defaultFacetDisplayValues}
                showCount={operator === FacetOperator.OR}
            />
            {canShowMore && (
                <ShowMoreComponent
                    showMore={handleToggleShowMore}
                    isShowingMore={isShowingMore}
                />
            )}
        </>
    );
};

export { DefaultFacet };
